import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { deriveFileNameFromUrl, detectAttachmentProvider } from './attachment-provider.util';

@Component({
  selector: 'app-attachment-link-modal',
  templateUrl: './attachment-link-modal.component.html',
  styleUrls: ['./attachment-link-modal.component.scss']
})
export class AttachmentLinkModalComponent implements OnInit, OnDestroy {
  @Input() linkToEdit: { url: string; fileName: string } | null = null;
  form: FormGroup;
  saving = false;
  fileNameTouched = false;
  providerName = '';
  private ngUnsubscribe = new Subject<void>();

  constructor(public modal: NgbActiveModal, private snackBar: MatSnackBar) {}

  get isEditMode(): boolean {
    return !!this.linkToEdit;
  }

  ngOnInit() {
    const initialUrl = this.linkToEdit ? this.linkToEdit.url || '' : '';
    const initialFileName = this.linkToEdit ? this.linkToEdit.fileName || '' : '';

    this.form = new FormGroup({
      url: new FormControl(initialUrl, [Validators.required, this.urlValidator]),
      fileName: new FormControl(initialFileName, [Validators.required])
    });

    if (this.isEditMode) {
      this.fileNameTouched = true;
      if (initialUrl) {
        this.providerName = detectAttachmentProvider(initialUrl).name;
      }
    }

    this.form
      .get('fileName')
      .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        if (this.form.get('fileName').dirty) this.fileNameTouched = true;
      });

    this.form
      .get('url')
      .valueChanges.pipe(debounceTime(400), takeUntil(this.ngUnsubscribe))
      .subscribe(value => this.onUrlChanged(value));
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private urlValidator(control: FormControl) {
    const value = (control.value || '').trim();
    if (!value) return null;
    try {
      const parsed = new URL(value);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return { invalidUrl: true };
      }
      return null;
    } catch (e) {
      return { invalidUrl: true };
    }
  }

  private onUrlChanged(value: string) {
    const trimmed = (value || '').trim();
    if (!trimmed || this.form.get('url').invalid) {
      this.providerName = '';
      return;
    }
    this.providerName = detectAttachmentProvider(trimmed).name;
    if (this.fileNameTouched && (this.form.get('fileName').value || '').trim()) return;
    this.form.get('fileName').setValue(deriveFileNameFromUrl(trimmed), { emitEvent: false });
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.modal.close({
      url: (this.form.value.url || '').trim(),
      fileName: (this.form.value.fileName || '').trim()
    });
  }
}
